import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Typography } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useFocusOnMount } from '../../../../utilities/customHooks/useFocusOnMount';
import { Neutrals, Primary } from '../../color-palette/colors';
import { Button, IconButton } from '../Buttons';
import { fillColumnSx, PROSE_MAX } from '../Screen/screenLayout';
import { StickyActionBar } from '../StickyActionBar';
import type { StickyActionBarTone } from '../StickyActionBar';

type StepState = 'done' | 'active' | 'ahead';
export type WizardEnterFrom = 'forward' | 'back' | 'none';

const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';

const stepStateOf = (index: number, activeStep: number): StepState => {
  if (index < activeStep) return 'done';
  if (index === activeStep) return 'active';
  return 'ahead';
};

const fill = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;

const enter = keyframes`
  from { opacity: 0; transform: translateX(var(--wizard-enter-from)); }
  to { opacity: 1; transform: none; }
`;

const segmentSx =
  (state: StepState) =>
  (theme: Theme): SystemStyleObject<Theme> => ({
    position: 'relative',
    flex: 1,
    blockSize: theme.spacing(1),
    borderRadius: 3,
    overflow: 'hidden',
    backgroundColor: state === 'done' ? Primary[400] : Neutrals[300],
    ...(state === 'active'
      ? {
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundColor: Primary[500],
            transformOrigin: 'left center',
            animation: `${fill} ${theme.transitions.duration.shorter}ms ${theme.transitions.easing.easeOut} both`,
          },
          '[dir="rtl"] &::after': { transformOrigin: 'right center' },
          [REDUCED_MOTION]: { '&::after': { animation: 'none' } },
        }
      : {}),
  });

const SEGMENT_SX: Readonly<Record<StepState, ReturnType<typeof segmentSx>>> = {
  done: segmentSx('done'),
  active: segmentSx('active'),
  ahead: segmentSx('ahead'),
};

const rootSx = [fillColumnSx, { inlineSize: '100%' }] as const;
const headerSx = { display: 'flex', alignItems: 'center', gap: 2, minInlineSize: 0 } as const;
const progressSx = { display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minInlineSize: 0 } as const;
const counterSx = { color: Neutrals[600], fontVariantNumeric: 'tabular-nums' } as const;
const segmentsSx = { display: 'flex', gap: 1, alignItems: 'center' } as const;
const headerActionSx = { flexShrink: 0, marginInlineStart: 'auto' } as const;
const backArrowSx = { display: { md: 'none' } } as const;
const dockBackSx = { display: { xs: 'none', md: 'flex' } } as const;
const titleBlockSx = { display: 'flex', flexDirection: 'column', gap: 2, marginBlockStart: 4 } as const;
const titleRowSx = { display: 'flex', alignItems: 'center', gap: 2, minInlineSize: 0 } as const;
const descriptionSx = { color: Neutrals[700], maxWidth: PROSE_MAX } as const;

const titleSx = (theme: Theme): SystemStyleObject<Theme> => ({
  fontSize: { xs: theme.typography.h1.fontSize, md: theme.typography.title.fontSize },
  lineHeight: theme.typography.lineHeightHeading,
  textWrap: 'balance',
  overflowWrap: 'anywhere',
  minInlineSize: 0,
  outline: 'none',
});

const STRETCH_FOR_STATE: SystemStyleObject<Theme> = {
  '&:has([data-placement])': { flex: '1 0 auto', display: 'flex', flexDirection: 'column' },
};

const bodySx =
  (enterFrom: WizardEnterFrom) =>
  (theme: Theme): SystemStyleObject<Theme> => ({
    minInlineSize: 0,
    ...STRETCH_FOR_STATE,
    ...(enterFrom === 'none'
      ? {}
      : {
          '--wizard-enter-from': enterFrom === 'forward' ? theme.spacing(4) : `calc(-1 * ${theme.spacing(4)})`,
          animation: `${enter} ${theme.transitions.duration.shorter}ms ${theme.transitions.easing.easeOut}`,
          [REDUCED_MOTION]: { animation: 'none' },
        }),
  });

const bodyClipSx = (theme: Theme): SystemStyleObject<Theme> => ({
  minInlineSize: 0,
  ...STRETCH_FOR_STATE,
  marginBlockStart: 6,
  overflowX: 'clip',
  overflowClipMargin: theme.spacing(1),
});

const BODY_SX: Readonly<Record<WizardEnterFrom, ReturnType<typeof bodySx>>> = {
  forward: bodySx('forward'),
  back: bodySx('back'),
  none: bodySx('none'),
};

export interface WizardShellProps {
  stepCount: number;
  activeStep: number;
  title: React.ReactNode;
  titleAdornment?: React.ReactNode;
  description?: React.ReactNode;
  counterLabel?: string;
  progressLabel?: string;
  onBack?: () => void;
  backLabel?: string;
  headerAction?: React.ReactNode;
  primary?: React.ReactElement;
  secondary?: React.ReactElement;
  tertiary?: React.ReactElement;
  dockMessage?: React.ReactNode;
  dockTone?: StickyActionBarTone;
  enterFrom?: WizardEnterFrom;
  autoFocusTitle?: boolean;
  footer?: React.ReactNode;
  children: React.ReactNode;
}

const present = (node: React.ReactNode): boolean => node !== undefined && node !== null && node !== false;

export const WizardShell: React.FC<WizardShellProps> = ({
  stepCount,
  activeStep,
  title,
  titleAdornment,
  description,
  counterLabel,
  progressLabel,
  onBack,
  backLabel,
  headerAction,
  primary,
  secondary,
  tertiary,
  dockMessage,
  dockTone,
  enterFrom = 'none',
  autoFocusTitle = false,
  footer,
  children,
}) => {
  const { t } = useTranslation();
  const titleRef = useFocusOnMount<HTMLHeadingElement>(autoFocusTitle, activeStep);
  const counter =
    counterLabel ??
    t('coreComponents.wizard.stepCounter', 'Step {{current}} of {{total}}', { current: activeStep + 1, total: stepCount });
  const hasDock = Boolean(primary || secondary || tertiary) || present(dockMessage) || present(footer);

  return (
    <Box sx={rootSx}>
      <Box sx={headerSx}>
        {onBack && (
          <IconButton label={backLabel ?? t('common.back', 'Back')} onClick={onBack} edge="start" sx={backArrowSx}>
            <ArrowBackIcon />
          </IconButton>
        )}
        <Box role="group" aria-label={progressLabel ?? t('coreComponents.wizard.progress', 'Progress')} sx={progressSx}>
          <Typography variant="caption2" component="p" sx={counterSx}>
            {counter}
          </Typography>
          <Box sx={segmentsSx} aria-hidden>
            {Array.from({ length: stepCount }, (_, index) => (
              <Box key={index} sx={SEGMENT_SX[stepStateOf(index, activeStep)]} />
            ))}
          </Box>
        </Box>
        {present(headerAction) && <Box sx={headerActionSx}>{headerAction}</Box>}
      </Box>

      <Box sx={titleBlockSx}>
        <Box sx={titleRowSx}>
          {titleAdornment}
          <Typography ref={titleRef} variant="title" component="h1" tabIndex={-1} sx={titleSx}>
            {title}
          </Typography>
        </Box>
        {present(description) && (
          <Typography variant="body" component="p" sx={descriptionSx}>
            {description}
          </Typography>
        )}
      </Box>

      <Box sx={bodyClipSx}>
        <Box key={activeStep} sx={BODY_SX[enterFrom]}>
          {children}
        </Box>
      </Box>

      {hasDock && (
        <StickyActionBar
          layout="dock"
          primary={primary}
          secondary={secondary}
          tertiary={tertiary}
          start={
            onBack ? (
              <Box sx={dockBackSx}>
                <Button intent="secondary" onClick={onBack}>
                  {backLabel ?? t('common.back', 'Back')}
                </Button>
              </Box>
            ) : undefined
          }
          message={dockMessage}
          tone={dockTone}
          actions={footer}
        />
      )}
    </Box>
  );
};
